import React from 'react';
import { ExternalLink, Github, Calendar, Users } from 'lucide-react';

interface Project {
  id: number;
  title: string;
  description: string;
  technologies: string[];
  impact: string;
  link?: string;
  github?: string;
  period: string;
  teamSize: string;
}

const ProjectsSection: React.FC = () => {
  const projects: Project[] = [
    {
      id: 1,
      title: 'Enterprise Analytics Platform',
      description: 'Led the development of a comprehensive analytics platform that processes millions of data points daily, providing real-time insights for enterprise clients.',
      technologies: ['React', 'Python', 'AWS', 'PostgreSQL', 'Redis'],
      impact: 'Increased user engagement by 45% and reduced decision-making time by 60%',
      link: 'https://example.com/analytics',
      github: 'https://github.com/example/analytics',
      period: '2023',
      teamSize: '8 members'
    },
    {
      id: 2,
      title: 'Mobile Banking App',
      description: 'Product management for a next-generation mobile banking application with AI-powered financial insights and personalized recommendations.',
      technologies: ['React Native', 'Node.js', 'MongoDB', 'Machine Learning'],
      impact: 'Acquired 100K+ users in first 6 months with 4.8★ rating',
      link: 'https://example.com/banking',
      github: 'https://github.com/example/banking',
      period: '2022',
      teamSize: '6 members'
    },
    {
      id: 3,
      title: 'E-commerce Optimization Engine',
      description: 'Developed an intelligent recommendation engine that increased conversion rates through personalized user experiences and predictive analytics.',
      technologies: ['Vue.js', 'TensorFlow', 'Docker', 'Kubernetes'],
      impact: 'Boosted conversion rate by 32% and AOV by 18%',
      link: 'https://example.com/ecommerce',
      github: 'https://github.com/example/ecommerce',
      period: '2021',
      teamSize: '5 members'
    },
    {
      id: 4,
      title: 'Healthcare Patient Portal',
      description: 'Spearheaded the product strategy for a patient-centric healthcare platform connecting patients with providers and streamlining care coordination.',
      technologies: ['Angular', 'Java Spring', 'MySQL', 'HIPAA Compliance'],
      impact: 'Reduced patient wait times by 40% and improved satisfaction scores by 35%',
      link: 'https://example.com/healthcare',
      github: 'https://github.com/example/healthcare',
      period: '2020',
      teamSize: '7 members'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-beige-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-light text-gray-900 mb-4 flex items-center justify-center">
            <span className="font-math mr-4">∑</span>
            Projects Portfolio
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A collection of products I've managed and launched, each solving unique challenges through data-driven strategies and user-centric design.
          </p>
          <p className="text-lg font-math text-beige-600 mt-4">
            f(x) = ax² + bx + c where x = user needs
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {projects.map((project) => (
            <div
              key={project.id}
              className="bg-white rounded-lg shadow-sm border border-beige-200 overflow-hidden hover:shadow-md transition-shadow duration-300"
            >
              {/* Project Header */}
              <div className="p-6 border-b border-beige-100">
                <h3 className="text-xl font-medium text-gray-900 mb-2">
                  {project.title}
                </h3>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <div className="flex items-center">
                    <Calendar size={14} className="mr-1" />
                    {project.period}
                  </div>
                  <div className="flex items-center">
                    <Users size={14} className="mr-1" />
                    {project.teamSize}
                  </div>
                </div>
              </div>

              {/* Project Content */}
              <div className="p-6">
                <p className="text-gray-700 mb-4 leading-relaxed">
                  {project.description}
                </p>

                {/* Technologies */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Technologies:</h4>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-beige-100 text-beige-800 rounded text-xs"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Impact */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Impact:</h4>
                  <p className="text-sm text-gray-700 bg-beige-50 p-3 rounded border border-beige-200">
                    {project.impact}
                  </p>
                </div>

                {/* Links */}
                <div className="flex space-x-4">
                  {project.link && (
                    <a
                      href={project.link}
                      className="flex items-center text-beige-700 hover:text-beige-900 transition-colors text-sm font-medium"
                    >
                      <ExternalLink size={16} className="mr-1" />
                      Live Demo
                    </a>
                  )}
                  {project.github && (
                    <a
                      href={project.github}
                      className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium"
                    >
                      <Github size={16} className="mr-1" />
                      GitHub
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mathematical Pattern Footer */}
        <div className="mt-16 text-center">
          <div className="inline-block p-6 bg-white rounded-lg border border-beige-200">
            <p className="font-math text-beige-600 text-sm">
              Product Management = ∫(User Research × Data Analysis) dt + Strategy
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectsSection;
